import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useAuth } from "../../hooks/useAuth";
import {
  listPayments,
  listMemberRegistrationPayments,
  listClubRegistrationPayments,
  getMemberRegistrationPayment,
  getClubRegistrationPayment,
  listManualPaymentApprovals,
  getManualPaymentApproval,
  approveManualPaymentApproval,
  rejectManualPaymentApproval,
  uploadManualPaymentReceipt,
  getManualPaymentReceiptFile,
} from "../../services/paymentService";
import ReceiptFileInput from "../../components/common/ReceiptFileInput";
import {
  exportBookingPaymentsReport,
  exportMemberPaymentsReport,
  exportClubPaymentsReport,
  getFacilityPaymentTotals,
} from "../../services/reportService";
import {
  TOKENS,
  Icon,
  ICONS,
  Pill,
  EmptyState,
  Spinner,
  inputClass,
  labelClass,
  btnPrimary,
  btnSecondary,
  statusColor,
  Pagination,
  usePagination,
  DEFAULT_PAGE_SIZE,
} from "../../components/common/DashboardUI";

// Pull the top-level message out of an axios error.
function errMessage(err, fallback) {
  return err?.response?.data?.message || fallback;
}

// Opens a blob in a pre-opened tab (must be window.open'd synchronously
// inside the triggering click to avoid the popup blocker) — same pattern
// used for voucher downloads elsewhere (MembershipRenewals.jsx etc).
function openBlobInTab(blob, tab) {
  const url = URL.createObjectURL(blob);
  if (tab && !tab.closed) tab.location.href = url;
  else window.open(url, "_blank");
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

const fmtDateTime = (v) => {
  if (!v) return "—";
  const s = String(v).replace("T", " ");
  return s.length >= 16 ? s.slice(0, 16) : s;
};

// Keys here must match ManualPaymentApprovalController::paymentFor()'s
// return values exactly (not the raw payable_type column) — any payable_type
// it doesn't special-case falls through to the raw value there, and any key
// here that doesn't match falls through to the raw value here too.
const paymentForLabel = (v) =>
  ({
    member_registration: "Member Registration",
    club_registration: "Club Registration",
    membership_renewal: "Membership Renewal",
    club_renewal: "Club Renewal",
    id_card_reprint: "ID Card Reprint",
    facility_booking: "Facility Booking",
  }[v] || v);

// Humanizes /api/payments' own `payment_for` discriminator (e.g. "booking"
// -> "Booking", "member_registration" -> "Member Registration") for the
// Recent Payments table's Type column — a distinct vocabulary from
// paymentForLabel above, which matches ManualPaymentApprovalController's
// payment_for values specifically.
const humanizePaymentFor = (v) =>
  v
    ? String(v)
        .split("_")
        .filter(Boolean)
        .map((w) => w[0].toUpperCase() + w.slice(1))
        .join(" ")
    : "—";

// ── Modal shell (matches the pattern used across the admin panel) ───────────
function Modal({ title, onClose, children, size = "md" }) {
  const width = size === "xl" ? "max-w-5xl" : size === "lg" ? "max-w-2xl" : "max-w-md";
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className={`relative w-full ${width} bg-white rounded-2xl shadow-2xl p-6 max-h-[90vh] overflow-y-auto`}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition"
        >
          <Icon path={ICONS.x} className="w-5 h-5" width={2} />
        </button>
        <h2 className="text-lg font-bold mb-4 pr-8" style={{ color: TOKENS.NAVY }}>
          {title}
        </h2>
        {children}
      </div>
    </div>
  );
}

// ── Overview tab data ────────────────────────────────────────────────────────
// Backed by three real list endpoints: GET /api/payments (facility bookings),
// GET /api/registration-payments/members and GET /api/registration-payments/clubs
// (see paymentService.js). None of them return a pre-aggregated summary
// (totals/trend/category-breakdown), so the stat tiles, donut and registration
// breakdown below are computed client-side from the raw rows in
// usePaymentsOverview(). Two pieces genuinely have no backend data behind them
// and stay hardcoded on purpose: per-tile trend/sparkline (would need a
// time-series endpoint) and the booking payments breakdown by facility/court
// (the /api/payments row has no facility field to group by).

// Reuse the shared status colors (see DashboardUI's statusColor) instead of
// inventing new ones, so these stat tiles read as the same "paid / pending /
// failed" vocabulary as every Pill elsewhere in the admin panel.
const GOOD = statusColor("Paid");
const WARN = statusColor("Pending");
const BAD = statusColor("Failed");

// Styling only — value/count is computed in usePaymentsOverview() from real data.
const STAT_CARD_META = [
  { key: "total", label: "Total Payments", fg: TOKENS.PRIMARY, bg: TOKENS.LIGHT, icon: ICONS.payments, money: true },
  { key: "success", label: "Successful Payments", fg: GOOD.color, bg: GOOD.bg, icon: ICONS.check },
  { key: "pending", label: "Pending Payments", fg: WARN.color, bg: WARN.bg, icon: ICONS.clock },
];

// Two categorical colors for the donut — deliberately distinct from the
// green/amber/red status vocabulary above (this encodes payment TYPE, not
// payment STATE, so it must not read as another status pill). value/pct
// computed in usePaymentsOverview().
const CATEGORY_STYLE = {
  registration: { label: "Registration Payments", color: TOKENS.PRIMARY, icon: ICONS.users },
  booking: { label: "Booking Payments", color: "#1baf7a", icon: ICONS.bookings },
  // Catch-all for /api/payments rows whose payment_for isn't "booking" (e.g.
  // renewals, ID card reprints) — anything not already covered above.
  other: { label: "Other Payments", color: "#7c3aed", icon: ICONS.fees },
};

// Booking-payments-by-facility styling — value/rows come from the real
// GET /api/dashboard/bookings/by-facility/totals endpoint (see
// usePaymentsOverview's facilityBreakdown), gated on "view_reports" or
// "view_bookings" — a different permission from the "view_payments" the
// rest of this page reuses.
const BOOKING_BREAKDOWN_STYLE = { color: "#1baf7a", bg: "#e3f9ef" };

const money = (v) => `LKR ${Number(v || 0).toLocaleString("en-LK", { minimumFractionDigits: 2 })}`;

// /api/payments' payment_for spans six types, but only club_registration and
// club_renewal are actually paid by the club as an entity — everything else
// (member_registration, renewal, reprint, booking) is paid by the individual
// member, even when that member belongs to a club (which is why the row also
// carries a club_name). Preferring club_name unconditionally used to show
// e.g. "club C" as the Payer on a club member's own registration payment
// instead of their name — only club-level payment_for values should do that.
const CLUB_LEVEL_PAYMENT_FOR = ["club_registration", "club_renewal"];
const bookingPayerName = (p) => {
  const preferClub = CLUB_LEVEL_PAYMENT_FOR.includes(p.payment_for);
  return (preferClub ? p.club_name : p.payer_name) || p.payer_name || p.club_name || "—";
};

// Normalizes rows from the three source endpoints into one shape the Recent
// Payments table and the stat/breakdown computations below can share.
function normalizePaymentRows(bookings, memberPayments, clubPayments) {
  const bookingRows = bookings.map((p) => ({
    key: `booking-${p.id}`,
    ref: p.payment_reference,
    payer: bookingPayerName(p),
    type: "Booking Payment",
    typeLabel: humanizePaymentFor(p.payment_for),
    paymentFor: p.payment_for,
    category: "registration_group_excluded",
    displayCategory: "—",
    amount: Number(p.payable_amount) || 0,
    status: p.payment_status,
    date: p.paid_at || p.created_at,
    detail: { kind: "booking", data: p },
  }));

  const memberRows = memberPayments.map((p) => ({
    key: `member-${p.process_id}`,
    ref: p.payment_reference,
    payer: p.applicant?.full_name || "—",
    type: "Registration Payment",
    category: "member",
    displayCategory: "Member Registration",
    amount: Number(p.payable_amount) || 0,
    status: p.payment_status,
    date: p.paid_at || p.created_at,
    detail: { kind: "member", id: p.process_id },
  }));

  const clubRows = clubPayments.map((p) => ({
    key: `club-${p.process_id}`,
    ref: p.payment_reference,
    payer: p.club?.club_name || "—",
    type: "Registration Payment",
    category: "club",
    displayCategory: "Club Registration",
    amount: Number(p.payable_amount) || 0,
    status: p.payment_status,
    date: p.paid_at || p.created_at,
    detail: { kind: "club", id: p.process_id },
  }));

  return [...bookingRows, ...memberRows, ...clubRows].sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
}

const pct = (part, total) => (total > 0 ? Math.round((part / total) * 100) : 0);

// Fetches the three list endpoints (plus, when permitted, the facility
// payment totals) and derives every real number the Overview tab renders.
function usePaymentsOverview(canViewFacilityBreakdown) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [facilityTotals, setFacilityTotals] = useState(null); // { total, facilities } or null (no permission / not loaded)

  const fetchAll = useCallback(() => {
    setLoading(true);
    const calls = [listPayments(), listMemberRegistrationPayments(), listClubRegistrationPayments()];
    // Only call the facility-totals endpoint when the user actually has
    // view_reports|view_bookings — calling it without either 403s.
    if (canViewFacilityBreakdown) calls.push(getFacilityPaymentTotals());
    Promise.allSettled(calls).then(([bookings, members, clubs, facility]) => {
      if (bookings.status === "rejected" || members.status === "rejected" || clubs.status === "rejected") {
        toast.error("Some payment data could not be loaded.");
      }
      setRows(
        normalizePaymentRows(
          bookings.status === "fulfilled" ? bookings.value : [],
          members.status === "fulfilled" ? members.value : [],
          clubs.status === "fulfilled" ? clubs.value : []
        )
      );
      setFacilityTotals(facility?.status === "fulfilled" ? facility.value : null);
      setLoading(false);
    });
  }, [canViewFacilityBreakdown]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Recent Payments only shows /api/payments (facility booking) rows — `rows`
  // is already sorted by date descending, so filtering to just the booking
  // type preserves that order without a re-sort.
  const bookingRows = useMemo(() => rows.filter((r) => r.type === "Booking Payment"), [rows]);

  // The four stat tiles are scoped to /api/payments only (bookingRows), not
  // the combined rows (which also folds in the two registration-payment
  // endpoints) — Total Payments is the sum of every /api/payments row's
  // payable_amount regardless of status, Successful/Pending/Failed are counts
  // of that same set by status. Raw numbers (not pre-formatted strings) so
  // the Overview tab can animate them counting up from 0 to these targets —
  // see useRevealProgress below.
  const stats = useMemo(() => {
    const totalAmount = bookingRows.reduce((sum, r) => sum + r.amount, 0);
    return {
      total: totalAmount,
      success: bookingRows.filter((r) => r.status === "Paid").length,
      pending: bookingRows.filter((r) => r.status === "Pending").length,
      failed: bookingRows.filter((r) => r.status === "Failed").length,
    };
  }, [bookingRows]);

  const categorySegments = useMemo(() => {
    // All /api/payments rows (every payment_for), not just "booking" — this
    // is the authoritative grand total since that endpoint returns every
    // payment type. The "Booking Payments" segment itself only counts rows
    // whose payment_for is actually "booking".
    const apiPaymentsRows = rows.filter((r) => r.type === "Booking Payment");
    const sumApiPaymentsWhere = (paymentFor) =>
      apiPaymentsRows.filter((r) => r.paymentFor === paymentFor).reduce((s, r) => s + r.amount, 0);
    const bookingTotal = sumApiPaymentsWhere("booking");
    const registrationTotal = rows.filter((r) => r.type === "Registration Payment").reduce((s, r) => s + r.amount, 0);
    const grandTotal = apiPaymentsRows.reduce((s, r) => s + r.amount, 0);
    // Everything in /api/payments that isn't "booking", "club_registration",
    // or "member_registration" — e.g. renewals, ID card reprints — falls
    // into this catch-all segment. Excluding the two registration
    // payment_for values here (even though the Registration Payments
    // segment's own total above comes from the separate registration
    // endpoints, not from these rows) avoids double-counting them as Other.
    const otherTotal = grandTotal - bookingTotal - sumApiPaymentsWhere("club_registration") - sumApiPaymentsWhere("member_registration");
    return {
      grandTotal,
      segments: [
        { key: "registration", value: registrationTotal, pct: pct(registrationTotal, grandTotal), ...CATEGORY_STYLE.registration },
        { key: "booking", value: bookingTotal, pct: pct(bookingTotal, grandTotal), ...CATEGORY_STYLE.booking },
        { key: "other", value: otherTotal, pct: pct(otherTotal, grandTotal), ...CATEGORY_STYLE.other },
      ],
    };
  }, [rows]);

  const registrationBreakdown = useMemo(() => {
    const clubTotal = rows.filter((r) => r.category === "club").reduce((s, r) => s + r.amount, 0);
    const memberTotal = rows.filter((r) => r.category === "member").reduce((s, r) => s + r.amount, 0);
    const groupTotal = clubTotal + memberTotal;
    return {
      key: "registration",
      label: "Registration Payments",
      total: groupTotal,
      color: TOKENS.PRIMARY,
      bg: TOKENS.LIGHT,
      rows: [
        { label: "Club Registration", value: clubTotal, pct: pct(clubTotal, groupTotal), icon: ICONS.clubs },
        { label: "Member Registration", value: memberTotal, pct: pct(memberTotal, groupTotal), icon: ICONS.user },
      ],
    };
  }, [rows]);

  // Real facility breakdown (GET /api/dashboard/bookings/by-facility/totals)
  // — null when the user lacks view_reports|view_bookings, or before the
  // first fetch resolves; the Overview tab hides the whole group in that case
  // rather than showing stale/sample numbers.
  const facilityBreakdown = useMemo(() => {
    if (!facilityTotals) return null;
    const total = Number(facilityTotals.total?.paid_amount) || 0;
    return {
      key: "booking",
      label: "Booking Payments",
      total,
      ...BOOKING_BREAKDOWN_STYLE,
      rows: (facilityTotals.facilities || []).map((f) => ({
        label: f.facility_name,
        value: Number(f.paid_amount) || 0,
        pct: pct(Number(f.paid_amount) || 0, total),
        icon: ICONS.facility,
      })),
    };
  }, [facilityTotals]);

  return { rows, bookingRows, loading, stats, categorySegments, registrationBreakdown, facilityBreakdown, refetch: fetchAll };
}

// Drives the "count up from 0" reveal on the Overview tab: a single eased
// 0→1 clock that starts once real data is ready (`ready` flips from false to
// true when the initial fetch resolves) and is shared by every stat tile,
// the donut chart and the breakdown lists, so they all animate in lockstep
// and land on their real values at the same moment instead of drifting.
function useRevealProgress(ready, duration = 1100) {
  const [progress, setProgress] = useState(0);
  const playedRef = useRef(false);

  useEffect(() => {
    if (!ready || playedRef.current) return undefined;
    playedRef.current = true;
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      // easeOutCubic — fast start, gentle settle, matches the rest of the
      // app's transition feel rather than a linear tick-up.
      setProgress(1 - Math.pow(1 - t, 3));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [ready, duration]);

  return progress;
}

// Small decorative trend sparkline — not a precision chart, just the "up/down
// at a glance" accent used on each stat tile (see the reference mock).
function Sparkline({ points, color }) {
  const w = 100;
  const h = 32;
  const pad = 2;
  const max = Math.max(...points);
  const min = Math.min(...points);
  const range = max - min || 1;
  const stepX = (w - pad * 2) / (points.length - 1);
  const coords = points
    .map((v, i) => `${pad + i * stepX},${h - pad - ((v - min) / range) * (h - pad * 2)}`)
    .join(" ");
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-8" preserveAspectRatio="none" aria-hidden="true">
      <polyline points={coords} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity="0.8" />
    </svg>
  );
}

// trendDir/trendPct/spark are optional — there's no time-series endpoint
// behind these tiles yet, so real (backend-driven) tiles simply omit them
// rather than show a fabricated trend next to a real total. `value` is the
// final numeric target; `progress` (0→1, shared with the rest of the tab via
// useRevealProgress) scales it down for the count-up-from-0 reveal, and
// `money` picks whether it renders as currency or a plain integer count.
function StatTile({ label, value, loading, progress = 1, money: isMoney, trendDir, trendPct, fg, bg, icon, spark }) {
  const shown = loading ? "…" : isMoney ? money(value * progress) : Math.round(value * progress).toLocaleString();
  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-semibold truncate" style={{ color: fg }}>
            {label}
          </p>
          <p className="text-2xl font-extrabold mt-1" style={{ color: TOKENS.NAVY }}>
            {shown}
          </p>
          {trendDir && (
            <p className="text-xs text-gray-400 mt-1">
              <span className="font-semibold" style={{ color: trendDir === "up" ? GOOD.color : BAD.color }}>
                {trendDir === "up" ? "↑" : "↓"} {trendPct}
              </span>{" "}
              from last month
            </p>
          )}
        </div>
        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: bg }}>
          <Icon path={icon} className="w-5 h-5" stroke={fg} width={2} />
        </div>
      </div>
      {spark && <Sparkline points={spark} color={fg} />}
    </div>
  );
}

// CSS conic-gradient donut — no charting library needed for two segments.
// `progress` (0→1, from useRevealProgress) shrinks every wedge back toward 0
// and the leftover ring is filled gray, so the chart reads as growing in
// from an empty ring in lockstep with the stat tiles rather than popping in
// fully formed.
function DonutChart({ segments, totalLabel, totalValue, progress = 1 }) {
  let cursor = 0;
  const stops = segments
    .map((s) => {
      const start = cursor;
      cursor += s.pct * progress;
      return `${s.color} ${start}% ${cursor}%`;
    })
    .join(", ");
  const filledStops = cursor < 100 ? `${stops}, #e5e7eb ${cursor}% 100%` : stops;
  return (
    <div
      className="relative w-40 h-40 sm:w-44 sm:h-44 rounded-full flex-shrink-0"
      style={{ background: `conic-gradient(${filledStops})` }}
      title={segments.map((s) => `${s.label}: ${s.pct}%`).join(" · ")}
    >
      <div className="absolute inset-3 bg-white rounded-full flex flex-col items-center justify-center text-center px-2">
        <p className="text-xs text-gray-400">{totalLabel}</p>
        <p className="text-base font-extrabold leading-tight" style={{ color: TOKENS.NAVY }}>
          {totalValue}
        </p>
      </div>
    </div>
  );
}

function BreakdownGroup({ group, progress = 1 }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-bold flex items-center gap-2" style={{ color: TOKENS.NAVY }}>
          {group.label}
          {group.sample && (
            <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-gray-100 text-gray-400">
              Sample
            </span>
          )}
        </h4>
        <span className="text-sm font-bold" style={{ color: TOKENS.NAVY }}>
          {money(group.total * progress)}
        </span>
      </div>
      <div className="space-y-2.5">
        {group.rows.map((row) => (
          <div key={row.label} className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: group.bg }}>
              <Icon path={row.icon} className="w-4 h-4" stroke={group.color} width={1.8} />
            </div>
            <p className="text-sm text-gray-700 flex-1 min-w-0 truncate">{row.label}</p>
            <span className="text-sm font-semibold whitespace-nowrap" style={{ color: TOKENS.NAVY }}>
              {money(row.value * progress)}
            </span>
            <span
              className="text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap"
              style={{ backgroundColor: TOKENS.LIGHT, color: TOKENS.PRIMARY }}
            >
              {Math.round(row.pct * progress)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Manual Payment Approvals tab (Director/Admin acknowledgement of shroff
// receipts submitted from the Members/Clubs/Renewals/Reprints "Confirm
// Payment" tabs) ────────────────────────────────────────────────────────────
// List is gated by "view_manual_payment_approvals"; acknowledge/reject
// actions inside the detail modal are individually gated by
// "approve_manual_payment_receipts" / "reject_manual_payment_receipts" — see
// ManualPaymentApprovalController. Both actions are audit-only now: the
// underlying payment already completed when the receipt was submitted, so
// acknowledging doesn't finalize anything and rejecting doesn't reverse it.
//
// Receipt scans: every row carries has_receipt_file. Viewing streams the file
// through the authenticated GET .../{id}/receipt route (not the raw
// receipt_file_url, which is built from the backend's own APP_URL). Rows
// submitted before receipts became mandatory can be backfilled — or a wrong
// scan replaced — from the detail modal, gated by
// "submit_manual_payment_receipts" (canUploadReceipt). The "Missing receipt
// only" toggle maps to ?missing_receipt=1.
function ManualApprovalsTab({ canApprove, canReject, canUploadReceipt }) {
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("Pending");
  const [missingReceiptOnly, setMissingReceiptOnly] = useState(false);

  const [selected, setSelected] = useState(null); // approval_id of the open detail modal
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [acting, setActing] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectionNote, setRejectionNote] = useState("");
  const [viewingReceiptId, setViewingReceiptId] = useState(null);
  const [receiptFile, setReceiptFile] = useState(null);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [receiptUploadError, setReceiptUploadError] = useState("");

  const fetchApprovals = useCallback(() => {
    setLoading(true);
    const params = {};
    if (statusFilter) params.status = statusFilter;
    if (search.trim()) params.search = search.trim();
    if (missingReceiptOnly) params.missing_receipt = 1;
    listManualPaymentApprovals(params)
      .then(setApprovals)
      .catch((err) => toast.error(errMessage(err, "Could not load manual payment approvals.")))
      .finally(() => setLoading(false));
  }, [statusFilter, search, missingReceiptOnly]);

  // Missing receipts can be on rows of any status (most legacy rows are
  // already Acknowledged), so switching the toggle on widens the status
  // filter to "All" rather than silently hiding them behind "Pending".
  const toggleMissingReceiptOnly = (on) => {
    setMissingReceiptOnly(on);
    if (on) setStatusFilter("");
  };

  const handleViewReceipt = async (approvalId) => {
    if (viewingReceiptId) return;
    // Opened synchronously inside the click so the popup blocker allows it.
    const tab = window.open("", "_blank");
    setViewingReceiptId(approvalId);
    try {
      const blob = await getManualPaymentReceiptFile(approvalId);
      openBlobInTab(blob, tab);
    } catch (err) {
      tab?.close?.();
      toast.error(errMessage(err, "Could not open this receipt."));
    } finally {
      setViewingReceiptId(null);
    }
  };

  const handleUploadReceipt = async (e) => {
    e.preventDefault();
    if (!detail || !receiptFile || uploadingReceipt) return;
    setUploadingReceipt(true);
    setReceiptUploadError("");
    try {
      const result = await uploadManualPaymentReceipt(detail.approval_id, receiptFile);
      toast.success(result.message || "Payment receipt uploaded.");
      if (result.receipt) setDetail(result.receipt);
      setReceiptFile(null);
      fetchApprovals();
    } catch (err) {
      const fieldMsg = err?.response?.data?.errors?.receipt?.[0];
      setReceiptUploadError(fieldMsg || errMessage(err, "Could not upload this receipt."));
    } finally {
      setUploadingReceipt(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(fetchApprovals, 300);
    return () => clearTimeout(t);
  }, [fetchApprovals]);

  const openDetail = (approvalId) => {
    setSelected(approvalId);
    setDetail(null);
    setDetailLoading(true);
    setRejecting(false);
    setRejectionNote("");
    setReceiptFile(null);
    setReceiptUploadError("");
    getManualPaymentApproval(approvalId)
      .then(setDetail)
      .catch((err) => {
        toast.error(errMessage(err, "Could not load this approval request."));
        setSelected(null);
      })
      .finally(() => setDetailLoading(false));
  };

  const closeDetail = () => {
    setSelected(null);
    setDetail(null);
    setRejecting(false);
  };

  const handleApprove = async () => {
    if (!detail || acting) return;
    if (
      !window.confirm(
        "Acknowledge this receipt? This is for audit purposes only — the payment has already been completed and this action does not affect it."
      )
    )
      return;
    setActing(true);
    try {
      const result = await approveManualPaymentApproval(detail.approval_id);
      toast.success(result.message || "Receipt acknowledged.");
      closeDetail();
      fetchApprovals();
    } catch (err) {
      toast.error(errMessage(err, "Could not acknowledge this receipt."));
    } finally {
      setActing(false);
    }
  };

  const handleReject = async (e) => {
    e.preventDefault();
    if (!detail || acting || !rejectionNote.trim()) return;
    setActing(true);
    try {
      const result = await rejectManualPaymentApproval(detail.approval_id, rejectionNote.trim());
      toast.success(result.message || "Receipt rejected.");
      closeDetail();
      fetchApprovals();
    } catch (err) {
      toast.error(errMessage(err, "Could not reject this receipt."));
    } finally {
      setActing(false);
    }
  };

  const payableName = (d) => d?.payable?.member_name || d?.payable?.club_name || "—";

  const { page, setPage, totalPages, pageItems } = usePagination(approvals, {
    pageSize: DEFAULT_PAGE_SIZE,
    resetKey: `${search}|${statusFilter}|${missingReceiptOnly}`,
  });

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative flex-1 min-w-[200px]">
          <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400">
            <Icon path={ICONS.search} className="w-4 h-4" width={1.8} />
          </span>
          <input
            className={`${inputClass} pl-9`}
            placeholder="Search by receipt reference or submitted-by…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select className={`${inputClass} w-auto`} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All statuses</option>
          <option value="Pending">Pending</option>
          <option value="Approved">Acknowledged</option>
          <option value="Rejected">Rejected</option>
        </select>
        <label className="inline-flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
          <input
            type="checkbox"
            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            checked={missingReceiptOnly}
            onChange={(e) => toggleMissingReceiptOnly(e.target.checked)}
          />
          Missing receipt only
        </label>
      </div>

      <div className="bg-white rounded-2xl shadow-sm p-6">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-14 gap-3">
            <Spinner />
            <p className="text-sm text-gray-400">Loading…</p>
          </div>
        ) : approvals.length === 0 ? (
          <EmptyState icon={ICONS.payments} text="No manual payment approval requests match your filters." />
        ) : (
          <>
          <div className="overflow-x-auto -mx-6 px-6">
            <table className="w-full text-left min-w-[880px]">
              <thead>
                <tr className="text-xs text-gray-400 border-b border-gray-100">
                  <th className="py-2 pr-3 font-medium">Receipt Ref</th>
                  <th className="py-2 px-3 font-medium">Type</th>
                  <th className="py-2 px-3 font-medium">For</th>
                  <th className="py-2 px-3 font-medium">Amount</th>
                  <th className="py-2 px-3 font-medium">Submitted By</th>
                  <th className="py-2 px-3 font-medium">Submitted At</th>
                  <th className="py-2 px-3 font-medium">Status</th>
                  <th className="py-2 px-3 font-medium">Receipt</th>
                  <th className="py-2 pl-3 font-medium text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {pageItems.map((a) => (
                  <tr key={a.approval_id} className="border-b border-gray-50 last:border-0">
                    <td className="py-3 pr-3 text-sm font-mono text-gray-600 whitespace-nowrap">{a.receipt_reference}</td>
                    <td className="py-3 px-3 text-sm text-gray-600 whitespace-nowrap">{paymentForLabel(a.payment_for)}</td>
                    <td className="py-3 px-3 text-sm font-semibold whitespace-nowrap" style={{ color: TOKENS.NAVY }}>
                      {a.payable?.member_name || a.payable?.club_name || "—"}
                    </td>
                    <td className="py-3 px-3 text-sm text-gray-600 whitespace-nowrap">{money(a.amount_paid)}</td>
                    <td className="py-3 px-3 text-sm text-gray-600 whitespace-nowrap">{a.submitted_by_name || "—"}</td>
                    <td className="py-3 px-3 text-xs text-gray-500 whitespace-nowrap">{fmtDateTime(a.submitted_at)}</td>
                    <td className="py-3 px-3">
                      <Pill>{a.approval_status === "Approved" ? "Acknowledged" : a.approval_status}</Pill>
                    </td>
                    <td className="py-3 px-3 whitespace-nowrap">
                      {a.has_receipt_file ? (
                        <button
                          type="button"
                          onClick={() => handleViewReceipt(a.approval_id)}
                          disabled={viewingReceiptId === a.approval_id}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-blue-700 hover:text-blue-900 disabled:opacity-50"
                        >
                          <Icon path={ICONS.download} className="w-4 h-4" width={1.8} />
                          {viewingReceiptId === a.approval_id ? "Opening…" : "View"}
                        </button>
                      ) : (
                        <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-amber-50 text-amber-700">
                          Missing
                        </span>
                      )}
                    </td>
                    <td className="py-3 pl-3 text-right">
                      <button
                        onClick={() => openDetail(a.approval_id)}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-gray-800"
                      >
                        <Icon path={ICONS.eye} className="w-4 h-4" width={1.8} />
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="flex justify-center pt-5 mt-1 border-t border-gray-100">
              <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
            </div>
          )}
          </>
        )}
      </div>

      {selected && (
        <Modal title="Manual Payment Approval" onClose={closeDetail} size="lg">
          {detailLoading || !detail ? (
            <div className="flex flex-col items-center justify-center py-14 gap-3">
              <Spinner />
              <p className="text-sm text-gray-400">Loading…</p>
            </div>
          ) : (
            <div className="space-y-5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-base font-bold truncate" style={{ color: TOKENS.NAVY }}>
                    {payableName(detail)}
                  </p>
                  <p className="text-xs text-gray-400 truncate">{paymentForLabel(detail.payment_for)}</p>
                </div>
                <Pill>{detail.approval_status === "Approved" ? "Acknowledged" : detail.approval_status}</Pill>
              </div>

              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
                {[
                  ["Payment reference", detail.payment_reference],
                  ["Receipt reference", detail.receipt_reference],
                  ["Payable amount", money(detail.payable_amount)],
                  ["Amount paid", money(detail.amount_paid)],
                  ["Paid date", detail.paid_date],
                  ["Submitted by", detail.submitted_by_name],
                  ["Submitted at", fmtDateTime(detail.submitted_at)],
                  ["Note", detail.note],
                  ["Acknowledged by", detail.approved_by_name],
                  ["Acknowledged at", fmtDateTime(detail.approved_at)],
                  ["Rejected by", detail.rejected_by_name],
                  ["Rejected at", fmtDateTime(detail.rejected_at)],
                  ["Rejection note", detail.rejection_note],
                ]
                  .filter(([, v]) => v !== null && v !== undefined && v !== "")
                  .map(([label, value]) => (
                    <div key={label} className="min-w-0">
                      <dt className="text-xs text-gray-400">{label}</dt>
                      <dd className="text-sm font-medium text-gray-700 break-words" style={{ color: TOKENS.NAVY }}>
                        {value}
                      </dd>
                    </div>
                  ))}
              </dl>

              <div className="pt-4 border-t border-gray-100 space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-xs text-gray-400">Receipt scan</p>
                    <p className="text-sm font-medium" style={{ color: TOKENS.NAVY }}>
                      {detail.has_receipt_file ? "Attached" : "No receipt file attached"}
                    </p>
                  </div>
                  {detail.has_receipt_file && (
                    <button
                      type="button"
                      onClick={() => handleViewReceipt(detail.approval_id)}
                      disabled={viewingReceiptId === detail.approval_id}
                      className={`${btnSecondary} !w-auto px-4`}
                    >
                      <Icon path={ICONS.download} className="w-4 h-4" width={1.8} />
                      {viewingReceiptId === detail.approval_id ? "Opening…" : "View Receipt"}
                    </button>
                  )}
                </div>
                {canUploadReceipt && (
                  <form onSubmit={handleUploadReceipt} className="space-y-3">
                    <ReceiptFileInput
                      file={receiptFile}
                      onChange={setReceiptFile}
                      error={receiptUploadError}
                      label={detail.has_receipt_file ? "Replace receipt" : "Upload receipt"}
                    />
                    <button
                      type="submit"
                      disabled={uploadingReceipt || !receiptFile}
                      className={`${btnPrimary} !w-auto px-6`}
                    >
                      {uploadingReceipt ? "Uploading…" : detail.has_receipt_file ? "Replace Receipt" : "Upload Receipt"}
                    </button>
                  </form>
                )}
              </div>

              {detail.approval_status === "Pending" && (canApprove || canReject) && (
                <div className="pt-4 border-t border-gray-100 space-y-3">
                  {!rejecting ? (
                    <div className="flex flex-wrap gap-3">
                      {canApprove && (
                        <button
                          type="button"
                          onClick={handleApprove}
                          disabled={acting}
                          className={`${btnPrimary} !w-auto px-6`}
                        >
                          {acting ? "Acknowledging…" : "Acknowledge"}
                        </button>
                      )}
                      {canReject && (
                        <button
                          type="button"
                          onClick={() => setRejecting(true)}
                          disabled={acting}
                          className="inline-flex items-center justify-center gap-2 border border-red-200 text-red-600 hover:bg-red-50 font-semibold rounded-lg text-sm px-6 py-2.5 transition"
                        >
                          Reject
                        </button>
                      )}
                    </div>
                  ) : (
                    <form onSubmit={handleReject} className="space-y-3">
                      <p className="text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2">
                        Rejecting only flags this receipt for review — the payment has already been completed and
                        will not be reversed.
                      </p>
                      <div>
                        <label className={labelClass}>Rejection note</label>
                        <textarea
                          className={`${inputClass} resize-none`}
                          rows={2}
                          value={rejectionNote}
                          onChange={(e) => setRejectionNote(e.target.value)}
                          required
                          autoFocus
                        />
                      </div>
                      <div className="flex gap-3">
                        <button
                          type="submit"
                          disabled={acting || !rejectionNote.trim()}
                          className="inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg text-sm px-6 py-2.5 transition"
                        >
                          {acting ? "Rejecting…" : "Confirm Reject"}
                        </button>
                        <button type="button" onClick={() => setRejecting(false)} className={`${btnSecondary} !w-auto px-6`}>
                          Cancel
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              )}
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}

// Detail modal for a Recent Payments row. Booking rows already carry every
// field GET /api/payments returns, so no extra call is needed; member/club
// registration rows fetch the single-record detail endpoints
// (GET /api/registration-payments/members|clubs/{id}) on open.
function PaymentDetailModal({ row, onClose }) {
  const [data, setData] = useState(row.detail.kind === "booking" ? row.detail.data : null);
  const [loading, setLoading] = useState(row.detail.kind !== "booking");

  useEffect(() => {
    if (row.detail.kind === "booking") return;
    setLoading(true);
    const fetcher = row.detail.kind === "member" ? getMemberRegistrationPayment : getClubRegistrationPayment;
    fetcher(row.detail.id)
      .then(setData)
      .catch((err) => {
        toast.error(errMessage(err, "Could not load this payment's details."));
        onClose();
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row.detail.kind, row.detail.id]);

  const fields = useMemo(() => {
    if (!data) return [];
    if (row.detail.kind === "booking") {
      return [
        ["Payer", bookingPayerName(data)],
        ["Payer email", data.payer_email],
        ["Club", data.club_name],
        ["Payment reference", data.payment_reference],
        ["Payable amount", money(data.payable_amount)],
        ["Paid amount", money(data.paid_amount)],
        ["Paid at", fmtDateTime(data.paid_at)],
        ["Created at", fmtDateTime(data.created_at)],
      ];
    }
    if (row.detail.kind === "member") {
      return [
        ["Applicant", data.applicant?.full_name],
        ["Email", data.applicant?.email],
        ["NIC", data.applicant?.nic_number],
        ["Phone", data.applicant?.primary_phone_number],
        ["Requested club", data.applicant?.requested_club_name],
        ["Income", data.income_description],
        ["Payment reference", data.payment_reference],
        ["External reference", data.external_payment_reference],
        ["Payable amount", money(data.payable_amount)],
        ["Paid amount", money(data.paid_amount)],
        ["Process status", data.process_status],
        ["Paid at", fmtDateTime(data.paid_at)],
      ];
    }
    return [
      ["Club", data.club?.club_name],
      ["Reg. no", data.club?.reg_no],
      ["Phone", data.club?.primary_phone_number],
      ["No. of coaches", data.club?.no_of_coaches],
      ["Payment reference", data.payment_reference],
      ["External reference", data.external_payment_reference],
      ["Payable amount", money(data.payable_amount)],
      ["Paid amount", money(data.paid_amount)],
      ["Process status", data.process_status],
      ["Paid at", fmtDateTime(data.paid_at)],
    ];
  }, [data, row.detail.kind]);

  return (
    <Modal title="Payment Details" onClose={onClose} size="lg">
      {loading || !data ? (
        <div className="flex flex-col items-center justify-center py-14 gap-3">
          <Spinner />
          <p className="text-sm text-gray-400">Loading…</p>
        </div>
      ) : (
        <div className="space-y-5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-base font-bold truncate" style={{ color: TOKENS.NAVY }}>
                {row.payer}
              </p>
              <p className="text-xs text-gray-400 truncate">{typeOfMergedRow(row) || row.type}</p>
            </div>
            <Pill>{data.payment_status}</Pill>
          </div>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
            {fields
              .filter(([, v]) => v !== null && v !== undefined && v !== "")
              .map(([label, value]) => (
                <div key={label} className="min-w-0">
                  <dt className="text-xs text-gray-400">{label}</dt>
                  <dd className="text-sm font-medium text-gray-700 break-words" style={{ color: TOKENS.NAVY }}>
                    {value}
                  </dd>
                </div>
              ))}
          </dl>
        </div>
      )}
    </Modal>
  );
}

// Shared table body for both "View All Payments" and "View All Transactions"
// — each modal owns its own independent Reference/Type/Status filters rather
// than inheriting whatever's active on the Recent Payments table outside, so
// reopening either popup always starts from its full base row set. `typeOf`
// lets each caller decide how a row's Type column/filter value is derived
// (the two modals show different row shapes — see call sites below).
function FilterableTransactionsTable({ rows, typeOf, onSelectRow }) {
  const [refFilter, setRefFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const typeOptions = useMemo(() => {
    const seen = new Set();
    rows.forEach((p) => {
      const t = typeOf(p);
      if (t) seen.add(t);
    });
    return Array.from(seen).sort();
  }, [rows, typeOf]);

  const filtered = useMemo(() => {
    const q = refFilter.trim().toLowerCase();
    return rows.filter((p) => {
      const matchesRef = !q || p.ref?.toLowerCase().includes(q);
      const matchesType = !typeFilter || typeOf(p) === typeFilter;
      const matchesStatus = !statusFilter || p.status === statusFilter;
      return matchesRef && matchesType && matchesStatus;
    });
  }, [rows, refFilter, typeFilter, statusFilter, typeOf]);

  const { page, setPage, totalPages, pageItems } = usePagination(filtered, {
    pageSize: DEFAULT_PAGE_SIZE,
    resetKey: `${refFilter}|${typeFilter}|${statusFilter}`,
  });

  return (
    <>
      <div className="flex flex-wrap items-center gap-3 -mt-3 mb-4">
        <div className="relative flex-1 min-w-[180px]">
          <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400">
            <Icon path={ICONS.search} className="w-4 h-4" width={1.8} />
          </span>
          <input
            className={`${inputClass} pl-9`}
            placeholder="Filter by reference number…"
            value={refFilter}
            onChange={(e) => setRefFilter(e.target.value)}
          />
        </div>
        <select className={`${inputClass} w-auto`} value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
          <option value="">All types</option>
          {typeOptions.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <select className={`${inputClass} w-auto`} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All statuses</option>
          <option value="Paid">Paid</option>
          <option value="Pending">Pending</option>
          <option value="Failed">Failed</option>
        </select>
      </div>
      <p className="text-xs text-gray-400 mb-3">
        {filtered.length} of {rows.length} payment{rows.length === 1 ? "" : "s"}
      </p>
      {filtered.length === 0 ? (
        <EmptyState icon={ICONS.payments} text="No payments match your filters." />
      ) : (
        <div className="overflow-x-auto max-h-[60vh] overflow-y-auto -mx-6 px-6">
          <table className="w-full text-left min-w-[760px]">
            <thead className="sticky top-0 bg-white">
              <tr className="text-xs text-gray-400 border-b border-gray-100">
                <th className="py-2 pr-3 font-medium">Reference</th>
                <th className="py-2 px-3 font-medium">Payer</th>
                <th className="py-2 px-3 font-medium">Type</th>
                <th className="py-2 px-3 font-medium">Amount</th>
                <th className="py-2 px-3 font-medium">Status</th>
                <th className="py-2 px-3 font-medium">Date</th>
                <th className="py-2 pl-3 font-medium text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {pageItems.map((p) => (
                <tr key={p.key} className="border-b border-gray-50 last:border-0">
                  <td className="py-3 pr-3 text-sm font-mono text-gray-600 whitespace-nowrap">{p.ref}</td>
                  <td className="py-3 px-3 text-sm font-semibold whitespace-nowrap" style={{ color: TOKENS.NAVY }}>
                    {p.payer}
                  </td>
                  <td className="py-3 px-3 text-sm text-gray-600 whitespace-nowrap">{typeOf(p)}</td>
                  <td className="py-3 px-3 text-sm text-gray-600 whitespace-nowrap">{money(p.amount)}</td>
                  <td className="py-3 px-3">
                    <Pill>{p.status}</Pill>
                  </td>
                  <td className="py-3 px-3 text-xs text-gray-500 whitespace-nowrap">{fmtDateTime(p.date)}</td>
                  <td className="py-3 pl-3 text-right">
                    <button
                      onClick={() => onSelectRow(p)}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-gray-800"
                    >
                      <Icon path={ICONS.eye} className="w-4 h-4" width={1.8} />
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {totalPages > 1 && (
        <div className="flex justify-center pt-4 mt-1 border-t border-gray-100">
          <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
        </div>
      )}
    </>
  );
}

const typeOfMergedRow = (p) => (p.type === "Booking Payment" ? p.typeLabel : p.displayCategory);
const typeOfBookingRow = (p) => p.typeLabel;

// "View All Payments" — the complete merged ledger behind the Payment
// Categories donut (all three sources: /api/payments +
// /registration-payments/members + /registration-payments/clubs), not just
// the /api/payments-only rows "View All Transactions" shows.
function AllPaymentsModal({ rows, onClose, onSelectRow }) {
  return (
    <Modal title="All Payments" onClose={onClose} size="xl">
      <FilterableTransactionsTable rows={rows} typeOf={typeOfMergedRow} onSelectRow={onSelectRow} />
    </Modal>
  );
}

// "View All Transactions" — the backend already returns every row unpaginated
// (PaymentController::index has no page/per_page param, just a plain ->get()),
// so this is purely a frontend expansion of bookingRows with its own filters,
// independent of whatever's active on the Recent Payments table outside.
function AllTransactionsModal({ rows, onClose, onSelectRow }) {
  return (
    <Modal title="All Transactions" onClose={onClose} size="xl">
      <FilterableTransactionsTable rows={rows} typeOf={typeOfBookingRow} onSelectRow={onSelectRow} />
    </Modal>
  );
}

// ── Report types — single source of truth for both the Type filter's three
// report-group options and the Download Report button below. Each group's
// `paymentFor` list is which /api/payments payment_for values that report
// covers (per PaymentController::index's own `type` vocabulary), used to
// filter the Recent Payments table when that group is selected. There's no
// single "export everything" backend route (see reportService.js for the
// endpoints and the note that none of the three are implemented there yet),
// so Download Report always exports whichever one of these three is
// currently selected in the Type filter — defaulting to Booking Payments
// when "All Types" (or nothing) is selected.
const REPORT_TYPES = [
  { key: "booking", label: "Booking Payments", paymentFor: ["booking"], run: exportBookingPaymentsReport },
  {
    key: "member",
    label: "Member Registration & Renewal",
    paymentFor: ["member_registration", "renewal"],
    run: exportMemberPaymentsReport,
  },
  {
    key: "club",
    label: "Club Registration & Renewal",
    paymentFor: ["club_registration", "club_renewal"],
    run: exportClubPaymentsReport,
  },
];

function DownloadReportButton({ typeFilter }) {
  const [downloading, setDownloading] = useState(false);
  const report = REPORT_TYPES.find((r) => r.key === typeFilter) || REPORT_TYPES[0];

  const handleClick = async () => {
    if (downloading) return;
    const tab = window.open("", "_blank");
    setDownloading(true);
    try {
      const blob = await report.run();
      openBlobInTab(blob, tab);
    } catch (err) {
      tab?.close?.();
      toast.error(errMessage(err, `Could not export the ${report.label} report.`));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={downloading}
      className="inline-flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white font-semibold rounded-lg text-sm px-4 py-2.5 transition shadow-sm disabled:opacity-60"
    >
      <Icon path={ICONS.download} className="w-4 h-4" width={1.8} />
      {downloading ? "Exporting…" : "Download Report"}
    </button>
  );
}

// ── Date range filter (Recent Payments table only — /api/payments has no
// date query param, see PaymentController::index — so this filters the
// already-fetched rows client-side by their `date` field, same as
// search/status below). ───────────────────────────────────────────────────
function DateRangeMenu({ from, to, onChange }) {
  const [open, setOpen] = useState(false);
  const [draftFrom, setDraftFrom] = useState(from);
  const [draftTo, setDraftTo] = useState(to);

  const openMenu = () => {
    setDraftFrom(from);
    setDraftTo(to);
    setOpen(true);
  };

  const fmt = (iso) => {
    const d = new Date(`${iso}T00:00:00`);
    return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };
  const label = from && to ? `${fmt(from)} – ${fmt(to)}` : from ? `From ${fmt(from)}` : to ? `Until ${fmt(to)}` : "All dates";

  const apply = () => {
    onChange({ from: draftFrom, to: draftTo });
    setOpen(false);
  };
  const clear = () => {
    setDraftFrom("");
    setDraftTo("");
    onChange({ from: "", to: "" });
    setOpen(false);
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => (open ? setOpen(false) : openMenu())}
        className={`inline-flex items-center gap-2 border font-semibold rounded-lg text-sm px-4 py-2.5 transition ${
          from || to ? "border-blue-300 text-blue-700 bg-blue-50" : "border-gray-300 text-gray-700 hover:bg-gray-50"
        }`}
      >
        <Icon path={ICONS.bookings} className="w-4 h-4" width={1.8} />
        {label}
        <Icon path={ICONS.chevron} className="w-3.5 h-3.5" width={2} />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute left-0 mt-2 w-72 max-w-[calc(100vw-2rem)] bg-white rounded-xl shadow-lg border border-gray-100 z-20 p-4 space-y-3">
            <div>
              <label className={labelClass}>From</label>
              <input type="date" className={inputClass} value={draftFrom} onChange={(e) => setDraftFrom(e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>To</label>
              <input type="date" className={inputClass} value={draftTo} onChange={(e) => setDraftTo(e.target.value)} />
            </div>
            <div className="flex gap-2 pt-1">
              <button type="button" onClick={apply} className={`${btnPrimary} !w-auto px-4`}>
                Apply
              </button>
              <button type="button" onClick={clear} className={`${btnSecondary} !w-auto px-4`}>
                Clear
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── Payment-type filter — the three REPORT_TYPES groups (Booking Payments /
// Member Registration & Renewal / Club Registration & Renewal), the same
// selection Download Report reads to decide which report to export.
// Filtered client-side here rather than via a second server call, matching
// search/status above.
function TypeFilterMenu({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const active = value !== "";
  const activeLabel = REPORT_TYPES.find((r) => r.key === value)?.label;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-2 border font-semibold rounded-lg text-sm px-4 py-2.5 transition ${
          active ? "border-blue-300 text-blue-700 bg-blue-50" : "border-gray-300 text-gray-700 hover:bg-gray-50"
        }`}
      >
        <Icon path={ICONS.filter} className="w-4 h-4" width={1.8} />
        {active ? `Type: ${activeLabel}` : "Filters"}
        <Icon path={ICONS.chevron} className="w-3.5 h-3.5" width={2} />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute left-0 mt-2 w-64 max-w-[calc(100vw-2rem)] bg-white rounded-xl shadow-lg border border-gray-100 z-20 overflow-hidden py-1">
            <button
              type="button"
              onClick={() => {
                onChange("");
                setOpen(false);
              }}
              className={`w-full text-left px-4 py-2 text-sm transition ${
                !active ? "font-semibold text-blue-700 bg-blue-50" : "text-gray-700 hover:bg-gray-50"
              }`}
            >
              All Types
            </button>
            {REPORT_TYPES.map((r) => (
              <button
                key={r.key}
                type="button"
                onClick={() => {
                  onChange(r.key);
                  setOpen(false);
                }}
                className={`w-full text-left px-4 py-2 text-sm transition ${
                  value === r.key ? "font-semibold text-blue-700 bg-blue-50" : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function PaymentsOverviewTab({ canViewFacilityBreakdown }) {
  const { rows, bookingRows, loading, stats, categorySegments, registrationBreakdown, facilityBreakdown } =
    usePaymentsOverview(canViewFacilityBreakdown);
  // Shared 0→1 reveal clock — starts once the initial fetch resolves, so the
  // stat tiles, donut chart and breakdown lists all count up from 0 together
  // and land on their real values at the same moment.
  const progress = useRevealProgress(!loading);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [detailRow, setDetailRow] = useState(null);
  const [showAllTransactions, setShowAllTransactions] = useState(false);
  const [showAllPayments, setShowAllPayments] = useState(false);

  const activeReportType = REPORT_TYPES.find((r) => r.key === typeFilter) || null;

  const filteredPayments = useMemo(() => {
    const q = search.trim().toLowerCase();
    const from = dateFrom ? new Date(`${dateFrom}T00:00:00`) : null;
    const to = dateTo ? new Date(`${dateTo}T23:59:59.999`) : null;
    return bookingRows.filter((p) => {
      const matchesSearch =
        !q ||
        p.ref?.toLowerCase().includes(q) ||
        p.payer?.toLowerCase().includes(q) ||
        p.displayCategory?.toLowerCase().includes(q);
      const matchesStatus = !statusFilter || p.status === statusFilter;
      const matchesType = !activeReportType || activeReportType.paymentFor.includes(p.paymentFor);
      const rowDate = p.date ? new Date(p.date) : null;
      const matchesDate = (!from && !to) || (rowDate && !Number.isNaN(rowDate.getTime()) && (!from || rowDate >= from) && (!to || rowDate <= to));
      return matchesSearch && matchesStatus && matchesType && matchesDate;
    });
  }, [bookingRows, search, statusFilter, activeReportType, dateFrom, dateTo]);

  const {
    page: recentPage,
    setPage: setRecentPage,
    totalPages: recentTotalPages,
    pageItems: recentPageItems,
  } = usePagination(filteredPayments, {
    pageSize: DEFAULT_PAGE_SIZE,
    resetKey: `${search}|${statusFilter}|${typeFilter}|${dateFrom}|${dateTo}`,
  });

  return (
    <div>
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative flex-1 min-w-[200px]">
          <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400">
            <Icon path={ICONS.search} className="w-4 h-4" width={1.8} />
          </span>
          <input
            className={`${inputClass} pl-9`}
            placeholder="Search by reference, payer or category…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select className={`${inputClass} w-auto`} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All statuses</option>
          <option value="Paid">Paid</option>
          <option value="Pending">Pending</option>
          <option value="Failed">Failed</option>
        </select>
        <DateRangeMenu from={dateFrom} to={dateTo} onChange={({ from, to }) => { setDateFrom(from); setDateTo(to); }} />
        <TypeFilterMenu value={typeFilter} onChange={setTypeFilter} />
        <DownloadReportButton typeFilter={typeFilter} />
      </div>

      {/* Stat tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-5">
        {STAT_CARD_META.map((c) => (
          <StatTile key={c.key} {...c} value={stats[c.key]} loading={loading} progress={progress} />
        ))}
      </div>

      {/* Categories + Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <h3 className="font-bold text-base" style={{ color: TOKENS.NAVY }}>
            Payment Categories
          </h3>
          <p className="text-xs text-gray-400 mb-5">Overview of payment types</p>
          <div className="flex flex-wrap items-center gap-6">
            <DonutChart
              segments={categorySegments.segments}
              totalLabel="Total (LKR)"
              totalValue={money(categorySegments.grandTotal * progress)}
              progress={progress}
            />
            <div className="flex-1 min-w-[180px] space-y-4">
              {categorySegments.segments.map((s) => (
                <div key={s.key} className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${s.color}1a` }}>
                    <Icon path={s.icon} className="w-5 h-5" stroke={s.color} width={1.8} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm text-gray-600">{s.label}</p>
                    <p className="text-sm font-bold" style={{ color: TOKENS.NAVY }}>
                      {money(s.value * progress)}
                    </p>
                    <p className="text-xs text-gray-400">{Math.round(s.pct * progress)}% of total</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setShowAllPayments(true)}
            className="w-full mt-5 inline-flex items-center justify-center gap-1.5 border border-gray-200 hover:bg-gray-50 text-gray-700 font-semibold rounded-lg text-sm px-4 py-2.5 transition"
          >
            View All Payments
            <Icon path={ICONS.arrowRight} className="w-4 h-4" width={2} />
          </button>
        </div>

        <div className="bg-white rounded-2xl shadow-sm p-6">
          <h3 className="font-bold text-base" style={{ color: TOKENS.NAVY }}>
            Payment Breakdown
          </h3>
          <p className="text-xs text-gray-400 mb-5">Detailed breakdown by payment type</p>
          <div className="space-y-6">
            <BreakdownGroup group={registrationBreakdown} progress={progress} />
            {/* Hidden (rather than shown empty/stale) when the user lacks
                view_reports|view_bookings, or before the fetch resolves. */}
            {canViewFacilityBreakdown && facilityBreakdown && <BreakdownGroup group={facilityBreakdown} progress={progress} />}
          </div>
          <button
            type="button"
            onClick={() => setShowAllPayments(true)}
            className="w-full mt-5 inline-flex items-center justify-center gap-1.5 border border-gray-200 hover:bg-gray-50 text-gray-700 font-semibold rounded-lg text-sm px-4 py-2.5 transition"
          >
            View Detailed Reports
            <Icon path={ICONS.arrowRight} className="w-4 h-4" width={2} />
          </button>
        </div>
      </div>

      {/* Recent payments */}
      <div className="bg-white rounded-2xl shadow-sm p-6">
        <h3 className="font-bold text-base" style={{ color: TOKENS.NAVY }}>
          Recent Payments
        </h3>
        <p className="text-xs text-gray-400 mb-4">Latest payment transactions</p>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-14 gap-3">
            <Spinner />
            <p className="text-sm text-gray-400">Loading…</p>
          </div>
        ) : filteredPayments.length === 0 ? (
          <EmptyState icon={ICONS.payments} text="No payments match your search." />
        ) : (
          <div className="overflow-x-auto -mx-6 px-6">
            <table className="w-full text-left min-w-[880px]">
              <thead>
                <tr className="text-xs text-gray-400 border-b border-gray-100">
                  <th className="py-2 pr-3 font-medium">Reference</th>
                  <th className="py-2 px-3 font-medium">Payer</th>
                  <th className="py-2 px-3 font-medium">Type</th>
                  <th className="py-2 px-3 font-medium">Amount</th>
                  <th className="py-2 px-3 font-medium">Status</th>
                  <th className="py-2 px-3 font-medium">Date</th>
                  <th className="py-2 pl-3 font-medium text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {recentPageItems.map((p) => (
                  <tr key={p.key} className="border-b border-gray-50 last:border-0">
                    <td className="py-3 pr-3 text-sm font-mono text-gray-600 whitespace-nowrap">{p.ref}</td>
                    <td className="py-3 px-3 text-sm font-semibold whitespace-nowrap" style={{ color: TOKENS.NAVY }}>
                      {p.payer}
                    </td>
                    <td className="py-3 px-3 text-sm text-gray-600 whitespace-nowrap">{p.typeLabel}</td>
                    <td className="py-3 px-3 text-sm text-gray-600 whitespace-nowrap">{money(p.amount)}</td>
                    <td className="py-3 px-3">
                      <Pill>{p.status}</Pill>
                    </td>
                    <td className="py-3 px-3 text-xs text-gray-500 whitespace-nowrap">{fmtDateTime(p.date)}</td>
                    <td className="py-3 pl-3 text-right">
                      <button
                        onClick={() => setDetailRow(p)}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-gray-800"
                      >
                        <Icon path={ICONS.eye} className="w-4 h-4" width={1.8} />
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {recentTotalPages > 1 && (
          <div className="flex justify-center pt-4 mt-1 border-t border-gray-100">
            <Pagination page={recentPage} totalPages={recentTotalPages} onPageChange={setRecentPage} />
          </div>
        )}

        <button
          type="button"
          onClick={() => setShowAllTransactions(true)}
          className="w-full mt-4 inline-flex items-center justify-center gap-1.5 border border-gray-200 hover:bg-gray-50 text-gray-700 font-semibold rounded-lg text-sm px-4 py-2.5 transition"
        >
          View All Transactions
          <Icon path={ICONS.arrowRight} className="w-4 h-4" width={2} />
        </button>
      </div>

      {showAllPayments && (
        <AllPaymentsModal rows={rows} onClose={() => setShowAllPayments(false)} onSelectRow={setDetailRow} />
      )}
      {showAllTransactions && (
        <AllTransactionsModal rows={bookingRows} onClose={() => setShowAllTransactions(false)} onSelectRow={setDetailRow} />
      )}
      {detailRow && <PaymentDetailModal row={detailRow} onClose={() => setDetailRow(null)} />}
    </div>
  );
}

export default function PaymentsManagement() {
  const { can } = useAuth();
  const canViewApprovals = can("view_manual_payment_approvals");
  const canApprove = can("approve_manual_payment_receipts");
  const canReject = can("reject_manual_payment_receipts");
  const canUploadReceipt = can("submit_manual_payment_receipts");
  const canViewFacilityPayments = can("view_reports") || can("view_bookings");
  const [activeTab, setActiveTab] = useState("overview");

  return (
    <div>
      {/* Header */}
      <div className="mb-5">
        <h1 className="text-xl font-bold" style={{ color: TOKENS.NAVY }}>
          Payments
        </h1>
        <p className="text-sm text-gray-500">Registration and facility booking payments.</p>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-2 mb-5 border-b border-gray-200">
        <button
          onClick={() => setActiveTab("overview")}
          className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition ${
            activeTab === "overview" ? "border-current" : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
          style={activeTab === "overview" ? { color: TOKENS.PRIMARY } : undefined}
        >
          Overview
        </button>
        {canViewApprovals && (
          <button
            onClick={() => setActiveTab("approvals")}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition ${
              activeTab === "approvals" ? "border-current" : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
            style={activeTab === "approvals" ? { color: TOKENS.PRIMARY } : undefined}
          >
            Manual Approvals
          </button>
        )}
      </div>

      {activeTab === "approvals" && canViewApprovals ? (
        <ManualApprovalsTab canApprove={canApprove} canReject={canReject} canUploadReceipt={canUploadReceipt} />
      ) : (
        <PaymentsOverviewTab canViewFacilityBreakdown={canViewFacilityPayments} />
      )}
    </div>
  );
}
